import { CurrencyPipe, DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import { HumanizeDuration, HumanizeDurationLanguage } from 'humanize-duration-ts';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

import { CostFormatPipe } from '@app/core/_base/layout/pipes/cost-format.pipe';
import { FileTypeConstant } from '@app/core/_constant/index.constants';
import { PermissionService } from '@app/core/auth/services/permission.service';
import { RfqService } from '@app/core/quotes';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { environment } from '@app/env-service';
import { EFeatureFlag } from '@app/feature-toggle-list';
import { FeatureFlagPipe } from 'v2/shared/feature/feature-flag.pipe';

import { EItemType } from './../../../v2/modules/order/interfaces/order.interface';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

@Injectable({
  providedIn: 'root',
})
export class ExportRfqService {
  clientConfig :any = environment.clientConfig;

  langService :HumanizeDurationLanguage = new HumanizeDurationLanguage();
  humanizer :HumanizeDuration = new HumanizeDuration(this.langService);

  excelFileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  excelFileExtension = '.xlsx';

  curPipe :CurrencyPipe;
  datePipe :DatePipe;
  costFormat :CostFormatPipe;
  featureFlag = EFeatureFlag;

  laborCosts :any = {};

  constructor(
    private rfqSvc :RfqService,
    private settingsSvc :SettingsService,
    private UserPermission :PermissionService,
    private featureFlagPipe :FeatureFlagPipe, // @TODO use featureToggleService instead
  ) {
    this.curPipe = new CurrencyPipe('en');
    this.datePipe = new DatePipe('en');
    this.costFormat = new CostFormatPipe();
    this.settingsSvc.settings$.subscribe((res :any) => {
      if (res && res.labor_costs) {
        this.laborCosts = res.labor_costs.reduce((acc, curr) => {
          return { ...acc, [curr.score]: curr.cost };
        }, {});
      }
    });
  }

  private saveExcelFile(buffer :any, fileName :string, ext ?:string) :void {
    const data :Blob = new Blob([buffer], { type: this.excelFileType });
    FileSaver.saveAs(data, fileName + (ext ? ext : this.excelFileExtension));
  }

  async exportRFQExcel(data :any) {
    const fileName :string = 'GMV_Report';

    // Records sheet
    const jsonData :any = [];
    data.salesRecord.forEach((row) => {
      jsonData.push({
        Date: {
          t: 'd',
          v: this.datePipe.transform(row.created_at, 'mediumDate'),
        },
        Client: row.business_name,
        Product: row.product_name,
        Factory: row.factory_name,
        'Sourcing Staff': row.staff,
        'Sales Staff': row.goteam,
        Units: row.total_quantity,
        Cost: { t: 'n', v: row.factory_cost, z: '$#,##0.00' },
        'Markup (%)': {
          t: 'n',
          v: row.markup_percent / 100,
          z: '0.00%',
        },
        Paid: { t: 'n', v: row.paid, z: '$#,##0.00' },
        Margin: {
          t: 'n',
          v: row.total_cost - row.factory_cost,
          z: '$#,##0.00',
          wch: 10,
        },
      });
    });
    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
    recordsSheet['A1'].s = { font: { bold: true } };
    recordsSheet['B1'].s = { font: { bold: true } };
    recordsSheet['C1'].s = { font: { bold: true } };
    recordsSheet['D1'].s = { font: { bold: true } };
    recordsSheet['E1'].s = { font: { bold: true } };
    recordsSheet['F1'].s = { font: { bold: true } };
    recordsSheet['G1'].s = { font: { bold: true } };
    recordsSheet['H1'].s = { font: { bold: true } };
    recordsSheet['I1'].s = { font: { bold: true } };
    recordsSheet['J1'].s = { font: { bold: true } };
    recordsSheet['K1'].s = { font: { bold: true } };
    recordsSheet['!cols'] = [
      { wch: 10 },
      { wch: 25 },
      { wch: 35 },
      { wch: 25 },
      { wch: 20 },
      { wch: 20 },
      { wch: 7 },
      { wch: 13 },
      { wch: 10 },
      { wch: 13 },
      { wch: 13 },
    ];

    let startRow :number = 2;
    const mergeRows :any[] = [];
    // Summary sheet
    const summaryData :any[] = [
      ['Total Summary', ''],
      [
        'Total Factory Cost',
        { t: 'n', v: data.salesTotal.factory_cost, z: '$#,##0.00' },
      ],
      ['Total Cost', { t: 'n', v: data.salesTotal.total_cost, z: '$#,##0.00' }],
      ['Total Paid', { t: 'n', v: data.salesTotal.total_paid, z: '$#,##0.00' }],
      [
        'Total Margin',
        {
          t: 'n',
          v: data.salesTotal.total_cost - data.salesTotal.factory_cost,
          z: '$#,##0.00',
        },
      ],
      [
        'Average Markup',
        { t: 'n', v: data.salesTotal.avg_markup / 100, z: '0.00%' },
      ],
    ];
    const summarySheet :XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([]);
    XLSX.utils.sheet_add_aoa(summarySheet, summaryData, {
      origin: 'A' + startRow.toString(),
    });
    summarySheet['!cols'] = [{ wch: 25 }, { wch: 15 }];
    mergeRows.push({ s: { r: 1, c: 0 }, e: { r: 1, c: 1 } });

    // staff commission
    startRow = +10;
    const staffCommData :any[] = [['Sourcing Team Commission']];
    data.staffCommission.forEach((com :any) => {
      staffCommData.push([
        com.staff,
        { t: 'n', v: com.commission, z: '$#,##0.00' },
      ]);
    });
    XLSX.utils.sheet_add_aoa(summarySheet, staffCommData, {
      origin: 'A' + startRow.toString(),
    });
    mergeRows.push({
      s: { r: startRow - 1, c: 0 },
      e: { r: startRow - 1, c: 1 },
    });

    // goteam commission
    startRow += staffCommData.length + 2;
    const goteamCommData :any[] = [['Sales Team Commission']];
    data.goteamCommission.forEach((com :any) => {
      goteamCommData.push([
        com.goteam,
        { t: 'n', v: com.commission, z: '$#,##0.00' },
      ]);
    });
    XLSX.utils.sheet_add_aoa(summarySheet, goteamCommData, {
      origin: 'A' + startRow.toString(),
    });
    mergeRows.push({
      s: { r: startRow - 1, c: 0 },
      e: { r: startRow - 1, c: 1 },
    });
    summarySheet['!merges'] = mergeRows;

    // create workbook
    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');
    // const wb: XLSX.WorkBook = { Sheets: { 'Records': ws }, SheetNames: ['Records'] };

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName);
  }

  async exportRFQPdf(data :any) {
    const logoUri = await this.getImgDataUrl('/assets/img/logo-print.jpg');

    const starredData :any[] = data.rows.filter(
      (row :any) => parseInt(row.starred) == 1,
    );
    const regularData :any[] = data.rows.filter(
      (row :any) => parseInt(row.starred) == 0,
    );
    const rfqsWithQuotes = data.rows.reduce(
      (count :number, row :any) => (count += row.count > 0 ? 1 : 0),
      0,
    );
    // regular table
    const regularTBData :any[] = [
      [
        { text: '' },
        { text: 'Date', bold: true, fillColor: '#eeeeee' },
        { text: 'Client / Grade', bold: true, fillColor: '#eeeeee' },
        { text: 'Product', bold: true, fillColor: '#eeeeee' },
        { text: 'Category', bold: true, fillColor: '#eeeeee' },
        {
          text: 'Units',
          alignment: 'right',
          bold: true,
          fillColor: '#eeeeee',
        },
        {
          text: 'Quotations',
          alignment: 'right',
          bold: true,
          fillColor: '#eeeeee',
        },
        {
          text: 'Time to First Quote',
          bold: true,
          fillColor: '#eeeeee',
        },
      ],
    ];
    regularData.forEach((row, idx) => {
      regularTBData.push([
        idx + 1,
        this.datePipe.transform(row.created_at, 'MM/dd/yyyy'),
        row.business_name + ' / ' + row.client_grade.toUpperCase(),
        row.product_name,
        row.product_category,
        { text: row.units_qty_first, alignment: 'right' },
        { text: row.count, alignment: 'right' },
        row.q_time > 0
          ? this.humanizer.humanize(row.q_time * 1000, {
              round: true,
              largest: 3,
            })
          : '',
        // this.humanizer.humanize(row.q_time * 1000, { round: true, largest: 3 })
      ]);
    });
    // starred table
    const starredTBData :any[] = [
      [
        { text: '' },
        { text: 'Date', bold: true, fillColor: '#eeeeee' },
        { text: 'Client / Grade', bold: true, fillColor: '#eeeeee' },
        { text: 'Product', bold: true, fillColor: '#eeeeee' },
        { text: 'Category', bold: true, fillColor: '#eeeeee' },
        {
          text: 'Units',
          alignment: 'right',
          bold: true,
          fillColor: '#eeeeee',
        },
        {
          text: 'Quotations',
          alignment: 'right',
          bold: true,
          fillColor: '#eeeeee',
        },
        {
          text: 'Time to First Quote',
          bold: true,
          fillColor: '#eeeeee',
        },
        { text: 'Pipeline GMV', bold: true, fillColor: '#eeeeee' },
      ],
    ];
    starredData.forEach((row, idx) => {
      starredTBData.push([
        idx + 1,
        this.datePipe.transform(row.created_at, 'MM/dd/yyyy'),
        row.business_name + ' / ' + row.client_grade.toUpperCase(),
        {
          text: [
            {
              text: row.product_name + '\n',
              style: { bold: true },
            },
            { text: row.starred_notes, style: { fontSize: 9 } },
          ],
        },
        row.product_category,
        { text: row.units_qty_first, alignment: 'right' },
        { text: row.count, alignment: 'right' },
        row.q_time > 0
          ? this.humanizer.humanize(row.q_time * 1000, {
              round: true,
              largest: 3,
            })
          : '',
        {
          text: this.curPipe.transform(row.pipeline_gmv, ''),
          alignment: 'right',
        },
      ]);
    });

    // prepare total
    const columns :any = [];
    columns.push([{ text: 'Date' }, { text: data.dt, style: 'summary' }]);
    columns.push([
      { text: 'Pipeline SKUs' },
      { text: starredData.length, style: 'summary' },
    ]);
    columns.push([
      { text: 'Starred SKUs Pipeline GMV' },
      {
        text: this.curPipe.transform(data.averagePipelineGMV, ''),
        style: 'summary',
      },
    ]);
    columns.push([
      { text: 'SKUs with Quotes' },
      { text: rfqsWithQuotes, style: 'summary' },
    ]);
    columns.push([
      { text: 'Ave Time to First Quote' },
      {
        text: this.humanizer.humanize(data.total.q_time * 1000, {
          round: true,
          largest: 3,
        }),
        style: 'summary',
      },
    ]);

    const cg :any[] = [
      [{ text: 'Total SKUs' }, { text: data.rows.length, style: 'summary' }],
    ];
    data.grade_total.forEach((gt) => {
      cg.push([
        { text: gt.client_grade.toUpperCase() + ' - clients' },
        { text: gt.rfq_count, style: 'summary' },
      ]);
    });

    const totalData :any[] = [
      {
        text: 'SKU Report Summary',
        alignment: 'center',
        style: 'header',
      },
      {
        text: 'Summary',
        style: 'summarytitle',
        margin: [10, 10, 10, 10],
      },
      {
        columns: columns,
      },
      { columns: cg },
    ];

    // prepare header
    const headerContent = [
      {
        columns: [
          {
            text: environment.clientConfig.companyName,
            alignment: 'left',
            style: 'companyheader',
          },
          { alignment: 'right', image: logoUri },
        ],
      },
    ];

    const starredTableContent :any[] = [
      {
        text: 'Starred SKUs',
        alignment: 'center',
        style: 'header',
        pageBreak: 'before',
      },
      {
        // style: 'gmvTable',
        table: {
          headerRows: 1,
          body: starredTBData,
        },
      },
    ];

    const regTableContent :any[] = [
      {
        text: 'Reqular SKUs',
        alignment: 'center',
        style: 'header',
        pageBreak: 'before',
      },
      {
        style: 'gmvTable',
        table: {
          headerRows: 1,
          body: regularTBData,
        },
      },
    ];

    let pdfContent :any[] = [];
    pdfContent = pdfContent.concat(
      headerContent,
      totalData,
      starredTableContent,
      regTableContent,
      {
        text: 'Generated: ' + this.datePipe.transform(new Date(), 'medium'),
      },
    );

    const documentDefinition = {
      pageSize: 'legal',
      pageOrientation: 'landscape',

      content: pdfContent,

      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        companyheader: {
          fontSize: 22,
          bold: true,
          margin: [0, 20, 0, 0],
          padding: [0, 0, 0, 0],
        },
        summarytitle: {
          fontSize: 14,
          bold: true,
        },
        header: {
          fontSize: 20,
          bold: true,
          margin: [0, 0, 0, 20],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 14,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        summary: {
          fontSize: 14,
          bold: false,
          margin: [10, 10, 10, 10],
          padding: [10, 10, 10, 10],
        },
        gmvTable: {
          margin: [0, 5, 0, 15],
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
        },
      },
    };
    Swal.close();
    pdfMake.createPdf(documentDefinition).download('rfq_report.pdf');
  }

  getImgDataUrl(imgUrl :string) {
    // Create canvas
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const baseImg = new Image();
      baseImg.src = imgUrl;
      baseImg.onload = function () {
        ctx.drawImage(baseImg, 0, 0, baseImg.width, baseImg.height);
        resolve(canvas.toDataURL());
      };
    });
  }

  exportRFQtoPDF(rfq :any, logistics :any) {
    // const logoUri = await this.getImgDataUrl('/assets/img/logo-print.png');
    this.rfqSvc.getRfqQuotations(rfq.id).subscribe((resp) => {
      this.getImgDataUrlValue(
        environment.clientConfig.assetImgDir + 'logo-print.png',
        (imgUri :any) => {
          this.creatPDF(rfq, resp.data, logistics, imgUri);
        },
      );
    });
  }

  creatPDF(rfq, quotations, logistics, logoUri) {
    // prepare table data
    const tableData :any[] = [
      [
        { text: 'Country', bold: true },
        { text: 'Tiered Pricing', bold: true },
        { text: 'Lead Time', bold: true },
        { text: 'Sample', bold: true },
      ],
    ];

    quotations.forEach((q) => {
      const tieredPricing :any = JSON.parse(q.tiered_pricing_json);

      const tpData :any[] = [
        [
          { text: 'Qty', bold: true },
          { text: logistics[q.logistic_1], bold: true },
          {
            text: q.logistic_2 ? logistics[q.logistic_2] : '',
            bold: true,
          },
        ],
      ];
      tieredPricing.forEach((tp :any) => {
        tpData.push([
          { text: tp.quantity, bold: true },
          {
            text: this.curPipe.transform(tp.logistic_1, ''),
            bold: true,
          },
          {
            text:
              tp.logistic_2 > 0 ? this.curPipe.transform(tp.logistic_2, '') : '',
            bold: true,
          },
        ]);
      });

      const data = [
        { text: q.country },
        {
          table: {
            headerRows: 1,
            body: tpData,
            layout: 'lightHorizontalLines',
          },
        },
        { text: q.lead_time_days_log1.toString() + ' days' },
        [
          { text: this.curPipe.transform(q.sample_price, '') },
          { text: q.sample_lead_time_days + ' days' },
        ],
      ];
      tableData.push(data);
    });
    const today = new Date();

    // prepare header
    const headerContent = [
      {
        columns: [
          {
            alignment: 'left',
            image: logoUri,
            width: 250,
            style: 'logo',
          },
          [
            {
              text: 'Request for Quote',
              alignment: 'right',
              style: 'header',
            },
            {
              text: 'As of ' + this.datePipe.transform(today, 'mediumDate'),
              alignment: 'right',
            },
          ],
        ],
      },
    ];

    const footerContent = [];

    const tableContent :any[] = [
      { text: rfq.product_name, style: 'product' },
      { text: rfq.product_category, style: 'category' },

      { text: 'Quotations', alignment: 'left', style: 'header' },
      {
        style: 'table',
        table: {
          widths: ['auto', '*', 'auto', 'auto'],
          headerRows: 1,
          body: tableData,
        },
        // layout: 'headerLineOnly'
      },
    ];

    let pdfContent :any[] = [];

    pdfContent = pdfContent.concat(headerContent, tableContent, footerContent);

    const documentDefinition = {
      pageSize: 'letter',
      pageOrientation: 'portrait',

      content: pdfContent,

      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        logo: {
          fillColor: '#ee0000',
        },
        product: {
          fontSize: 15,
          margin: [0, 20, 0, 5],
          bold: true,
        },
        category: {
          fontSize: 14,
        },
        shipping: {
          margin: [0, 25, 0, 0],
        },
        header: {
          fontSize: 16,
          bold: false,
          margin: [0, 30, 0, 5],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 14,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        table: {
          margin: [0, 5, 0, 15],
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
        },
      },
    };
    Swal.close();
    pdfMake.createPdf(documentDefinition).download('RequestForQuote.pdf');
  }

  getImgDataUrlValue(imgUrl :string, callback) {
    const xhr = new XMLHttpRequest();
    xhr.onload = function () {
      const reader = new FileReader();
      reader.onloadend = function () {
        callback(reader.result);
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', imgUrl);
    xhr.responseType = 'blob';
    xhr.send();
  }

  exportVariantSkuDetailsToExcel(
    rfqs :any[],
    includeAlternateSkus :boolean,
    tags :any[],
    shippingCarriers :any[],
  ) :void {
    const filename :string = 'variant-sku-details';
    // Records sheet
    const variantSkuDetailsData :any = [];

    let rfqList :any[] = [];
    rfqs.forEach((q) => {
      rfqList = rfqList.concat(q);
      if (q.variant_skus && q.variant_skus.length > 0) {
        rfqList = rfqList.concat(q.variant_skus);
      }
    });

    rfqList.forEach((sku, key) => {
      // SET DATA
      variantSkuDetailsData.push(
        this.getMasterVarSkuDetailsData({
          sku,
          includeAlternateSkus: false,
          shippingCarriers,
        }),
      );

      if (sku.tags && sku.tags.length) {
        let subTag = '';
        sku.tags.map((tag, index) => {
          if (tag && tag.tag_details && tag.tag_details.parent_id) {
            const matchedTag = tags.find(
              (sTag) => sTag.id === tag.tag_details.parent_id,
            );
            subTag = matchedTag ? matchedTag.name : '';
          }

          variantSkuDetailsData[key] = {
            ...variantSkuDetailsData[key],
            [`Tag_${index + 1}`]:
              tag && tag.tag_details ? tag.tag_details.name : '',
            [`Sub_Tags_${index + 1}`]: subTag,
          };
        });
      }
    });

    const masterSkuDetailsRecordSheet :XLSX.WorkSheet =
      XLSX.utils.json_to_sheet(variantSkuDetailsData);
    this.downloadExelFile(masterSkuDetailsRecordSheet, filename, '.xlsx');
    Swal.close();
  }

  exportMasterSkuDetailsToExcel(
    rfqs :any[],
    includeAlternateSkus :boolean,
    tags :any[],
    shippingCarriers :any[],
  ) :void {
    const filename :string = 'master-sku-details';
    // Records sheet
    const masterSkuDetailsData :any = [];

    let rfqList :any[] = [];
    rfqs.forEach((q) => {
      rfqList = rfqList.concat(q);
      if (q.variant_skus && q.variant_skus.length > 0) {
        rfqList = rfqList.concat(q.variant_skus);
      }
    });

    rfqList.forEach((sku, key) => {
      // SET DATA
      masterSkuDetailsData.push(
        this.getMasterVarSkuDetailsData({
          sku,
          includeAlternateSkus: false,
          shippingCarriers,
        }),
      );

      if (sku.tags && sku.tags.length) {
        let subTag = '';
        sku.tags.map((tag, index) => {
          if (tag && tag.tag_details && tag.tag_details.parent_id) {
            const matchedTag = tags.find(
              (sTag) => sTag.id === tag.tag_details.parent_id,
            );
            subTag = matchedTag ? matchedTag.name : '';
          }

          masterSkuDetailsData[key] = {
            ...masterSkuDetailsData[key],
            [`Tag_${index + 1}`]:
              tag && tag.tag_details ? tag.tag_details.name : '',
            [`Sub_Tags_${index + 1}`]: subTag,
          };
        });
      }
    });

    const masterSkuDetailsRecordSheet :XLSX.WorkSheet =
      XLSX.utils.json_to_sheet(masterSkuDetailsData);
    this.downloadExelFile(masterSkuDetailsRecordSheet, filename, '.xlsx');
    Swal.close();
  }

  exportMasterSkuToExcel(rfqs :any[], includeAlternateSkus :boolean = false) :void {
    const filename :string = includeAlternateSkus ? 'alternate-sku' : 'master-sku';
    // Records sheet
    const masterSkuData :any = [];

    let rfqList :any[] = [];
    rfqs.forEach((q) => {
      rfqList = rfqList.concat(q);
      if (q.variant_skus && q.variant_skus.length > 0) {
        rfqList = rfqList.concat(q.variant_skus);
      }
    });

    rfqList.forEach((q, key) => {
      // SET DATA
      masterSkuData.push(this.getMasterSkuData({ sku: q, includeAlternateSkus }));

      if (q.product_variants) {
        const rawVariants :any[] = JSON.parse(q.product_variants);

        if (rawVariants.length) {
          rawVariants.map((raw, index) => {
            const parsedData :any = this.getParseableData(raw.variant_terms);
            masterSkuData[key] = {
              ...masterSkuData[key],
              [`Variant_Type_${index + 1}`]: raw.variant_name,
              [`Variant_Data_${index + 1}`]:
                Array.isArray(parsedData) && parsedData?.length
                  ? parsedData
                      .reduce(
                        (acc, value) => (acc ? `${acc}, ${value}` : value),
                        '',
                      )
                      .join()
                  : '',
            };
          });
        }
      }
    });

    const masterSkusRecordSheet :XLSX.WorkSheet =
      XLSX.utils.json_to_sheet(masterSkuData);
    this.downloadExelFile(masterSkusRecordSheet, filename, '.xlsx');
    Swal.close();
  }

  exportAlternateSkuToExcel(rfqs :any[]) :void {
    const filename :string = 'alternate-sku';
    // Records sheet
    const alternateSkuData :any = [];

    let rfqList :any[] = [];
    rfqs.forEach((q) => {
      rfqList = rfqList.concat(q);
      if (q.variant_skus && q.variant_skus.length > 0) {
        rfqList = rfqList.concat(q.variant_skus);
      }
    });

    rfqList.forEach((sku, key) => {
      // SET DATA
      alternateSkuData.push(this.getAlternateSkuData({ sku }));

      if (sku.alternate_skus && sku.alternate_skus.length) {
        sku.alternate_skus.map((raw, index) => {
          alternateSkuData[key] = {
            ...alternateSkuData[key],
            [`Alternate_SKU_${index + 1}`]: raw.alternate_sku,
          };
        });
      }
    });

    const masterSkusRecordSheet :XLSX.WorkSheet =
      XLSX.utils.json_to_sheet(alternateSkuData);
    this.downloadExelFile(masterSkusRecordSheet, filename, '.xlsx');
    Swal.close();
  }

  exportVariantSkus(rfqs :any[]) :void {
    const filename :string = 'variant-sku';
    // Records sheet
    const variantSkuData :any = [];

    rfqs.forEach((sku, key) => {
      const materials :any = this.getParseableData(sku.materials);

      variantSkuData.push({
        Master_SKU: sku.parent ? sku.parent.internal_sku : '',
        Variant_SKU: sku.internal_sku || '',
        Product_Name: sku.product_name || '',
        Internal_Use: sku.internal_use_only ? 'YES' : 'NO',
        Product_Description: this.removeTagsOnString(sku.product_description) || '',
        Materials: Array.isArray(materials)
          ? materials.reduce(
              (acc, value) => (acc ? `${acc}, ${value.mv}` : value.mv),
              '',
            )
          : '',
        URL: `${window.location.origin}/inventory/${sku.id}/view`,
      });

      if (sku.product_variants) {
        let rawProdVariants :any[] = [];
        try {
          rawProdVariants = JSON.parse(sku.product_variants);
        } catch (e) {}

        if (
          Array.isArray(rawProdVariants) &&
          rawProdVariants.length > 0 &&
          rawProdVariants[0] instanceof Object
        ) {
          rawProdVariants.map((raw, index) => {
            variantSkuData[key] = {
              ...variantSkuData[key],
              [`Variant_Type_${index + 1}`]: raw.variant_name || '',
              [`Variant_Data_${index + 1}`]:
                (Array.isArray(raw.variant_terms)
                  ? raw.variant_terms.join(',')
                  : raw.variant_terms) || '',
            };
          });
        }
      }
    });

    const masterSkusRecordSheet :XLSX.WorkSheet =
      XLSX.utils.json_to_sheet(variantSkuData);
    this.downloadExelFile(masterSkusRecordSheet, filename, '.xlsx');
    Swal.close();
  }

  // EXPORT RFQS
  exportRfqsToCsv(rfqs :any[], includeAlternateSkus :boolean = false) {
    const fileName :string = includeAlternateSkus ? 'SKUsAlternateSKUs' : 'SKUs';
    // Records sheet
    const jsonData :any = [];

    let rfqList :any[] = [];
    rfqs.forEach((q) => {
      rfqList = rfqList.concat(q);
      if (q.variant_skus && q.variant_skus.length > 0) {
        rfqList = rfqList.concat(q.variant_skus);
      }
    });

    rfqList.forEach((q) => {
      if (q.stock) {
        q.qty_incoming = q.stock.qty_incoming < 0 ? 0 : q.stock.qty_incoming;
        q.qty_pending = q.stock.qty_pending < 0 ? 0 : q.stock.qty_pending;
        q.qty_available = q.stock.qty_onhand - q.stock.qty_pending;
        q.qty_onhand = q.stock.qty_onhand;
      }

      // q.unit_costs = this.rfqSvc.getUnitCosts(q.qty_available, q.latest_purchase_orders, (q.base_price ? q.base_price.price : 0));
      q.unit_costs = this.rfqSvc.getSkuUnitCosts(q);
      let weightedCost = q.unit_costs.computed_weighted;
      if (weightedCost == 0 && q.base_price && q.base_price.computed_weighted) {
        weightedCost = q.base_price.computed_weighted;
      }

      const inventoryCost :number = q.qty_available * weightedCost;

      let shippingWeight = '';
      if (q.shipping_weight) {
        try {
          shippingWeight = JSON.parse(q.shipping_weight)
            .map((row) => row.wv + ' ' + row.mu)
            .join(' ');
        } catch (e) {}
      }
      let dimensionalWeight = '';
      if (q.dimensional_weight) {
        try {
          dimensionalWeight = JSON.parse(q.dimensional_weight)
            .map((row) => row.wv + ' ' + row.mu)
            .join(' ');
        } catch (e) {}
      }

      jsonData.push({
        SKU: q.internal_sku,
        ...(includeAlternateSkus && {
          'Alternate SKUs': q.alternate_skus
            ? q.alternate_skus
            : []
                .map((sku) => sku.alternate_sku) // convert to string
                .join(', '),
        }),
        'Product Name': q.product_name,
        'Product Dept': q.product_dept,
        'Qty Incoming': q.qty_incoming,
        'Qty On-hand': q.qty_onhand,
        'Qty Pending': q.qty_pending,
        'Qty Available': q.qty_available,
        UOM: q?.unit_class?.name,
        'Package Cost': this.costFormat.transform(q.package_cost),
        'Labor Cost': this.costFormat.transform(
          q.efficiency_score && q.efficiency_score > 0
            ? this.laborCosts[q.efficiency_score]
            : 0,
        ),
        'Shipping Weight': shippingWeight,
        'Dimensional Weight': dimensionalWeight,

        'Weighted Unit Cost': this.costFormat.transform(
          weightedCost > 0 ? weightedCost : 0,
        ),
        'Inventory Cost': this.costFormat.transform(
          inventoryCost > 0 ? inventoryCost : 0,
        ),
        'Is Discontinued?': q.is_discontinued == 1 ? 'Discontinued' : 'Active',
        // "Alternate SKUs": q.alternate_skus
        //     .map((sku) => sku.alternate_sku)
        //     .join(", "),
        'Product Link': q.product_link,
        URL: window.location.origin + '/inventory/' + q.id + '/view',
      });
    });

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
    recordsSheet['A1'].s = { font: { bold: true } };
    recordsSheet['B1'].s = { font: { bold: true } };
    recordsSheet['C1'].s = { font: { bold: true } };
    recordsSheet['D1'].s = { font: { bold: true } };
    recordsSheet['E1'].s = { font: { bold: true } };
    recordsSheet['F1'].s = { font: { bold: true } };
    recordsSheet['G1'].s = { font: { bold: true } };
    recordsSheet['H1'].s = { font: { bold: true } };
    recordsSheet['I1'].s = { font: { bold: true } };
    recordsSheet['J1'].s = { font: { bold: true } };
    recordsSheet['K1'].s = { font: { bold: true } };
    recordsSheet['L1'].s = { font: { bold: true } };

    recordsSheet['!cols'] = [
      { wch: 25 },
      { wch: 60 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 60 },
      { wch: 60 },
    ];

    if (includeAlternateSkus) {
      recordsSheet['!cols'].splice(1, 0, { wch: 25 });
      recordsSheet['L1'].s = { font: { bold: true } };
    }

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  private downloadExelFile(
    recordSheet :XLSX.WorkSheet,
    filename :string,
    type :string,
  ) :void {
    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array',
    });

    this.saveExcelFile(excelBuffer, filename, type);
  }

  private getAlternateSkuData({ sku }) :any {
    return {
      Master_SKU: sku.internal_sku,
      Product_Name: sku.product_name,
    };
  }

  private getMasterSkuData({ sku, includeAlternateSkus }) :any {
    return {
      Master_SKU: sku.internal_sku,
      ...(includeAlternateSkus && {
        'Alternate SKUs': sku.alternate_skus
          .map((sku) => sku.alternate_sku) // convert to string
          .join(', '),
      }),
      Product_Name: sku.product_name,
      Product_Category: sku.product_category,
      Product_Sub_Category: sku.sub_category,
      Status: sku.status ? 'ACTIVE' : 'DISCONTINUED',
      Internal_Use: sku.internal_use_only ? 'YES' : 'NO',
      Product_Description: this.removeTagsOnString(sku.product_description),
      Materials: this.getParseableData(sku.materials).reduce(
        (acc, value) => (acc ? `${acc}, ${value.mv}` : value.mv),
        '',
      ),
      URL: `${window.location.origin}/inventory/${sku.id}/view`,
    };
  }

  private getDimensionStringFormat(dimension :{
    length :number;
    width :number;
    height :number;
  }) :string {
    const { length, width, height } = dimension;

    return `${length ? length : 0}x${width ? width : 0}x${height ? height : 0}`;
  }

  private getParseableData(value :string) :any {
    try {
      return value ? JSON.parse(value) : [];
    } catch (error) {
      return [value];
    }
  }

  private getMasterVarSkuDetailsData({
    sku,
    includeAlternateSkus,
    shippingCarriers,
  }) :any {
    const boxWeightArr = this.getParseableData(sku.box_weight);
    const productWeightArr = this.getParseableData(sku.product_weight);
    const shippingWeightArr = this.getParseableData(sku.shipping_weight);
    const cartonWeightArr = this.getParseableData(sku.carton_weight);
    const palletWeightArr = this.getParseableData(sku.pallet_weight);
    const productLinkArr = this.getParseableData(sku.product_link);
    const defaultShipping = shippingCarriers.find(
      (carrier) => carrier.id === sku.default_shipping,
    );

    let boxWeightStr = '';
    let productWeightStr = '';
    let shippingWeightStr = '';
    let cartonWeightStr = '';
    let palletWeightStr = '';
    let productLinkStr = '';

    if (boxWeightArr && typeof boxWeightArr === 'object') {
      boxWeightStr = boxWeightArr.reduce(
        (acc, value) =>
          acc ? `${acc}, ${value.wv}${value.mu}` : `${value.wv}${value.mu}`,
        '',
      );
    }

    if (productWeightArr && typeof productWeightArr === 'object') {
      productWeightStr = productWeightArr.reduce(
        (acc, value) =>
          acc ? `${acc}, ${value.wv}${value.mu}` : `${value.wv}${value.mu}`,
        '',
      );
    }

    if (shippingWeightArr && typeof shippingWeightArr === 'object') {
      shippingWeightStr = shippingWeightArr.reduce(
        (acc, value) =>
          acc ? `${acc}, ${value.wv}${value.mu}` : `${value.wv}${value.mu}`,
        '',
      );
    }

    if (cartonWeightArr && typeof cartonWeightArr === 'object') {
      cartonWeightStr = cartonWeightArr.reduce(
        (acc, value) =>
          acc ? `${acc}, ${value.wv}${value.mu}` : `${value.wv}${value.mu}`,
        '',
      );
    }

    if (palletWeightArr && typeof palletWeightArr === 'object') {
      palletWeightStr = palletWeightArr.reduce(
        (acc, value) =>
          acc ? `${acc}, ${value.wv}${value.mu}` : `${value.wv}${value.mu}`,
        '',
      );
    }

    if (
      productLinkArr &&
      typeof productLinkArr === 'object' &&
      productLinkArr.length
    ) {
      productLinkStr = productLinkArr.reduce(
        (acc, value) => (acc ? `${acc}, ${value.lv}` : value.lv),
        '',
      );
    }

    const skuData :any = {
      Master_Variant_SKU: sku.internal_sku,
      Product_Name: sku.product_name,
      ...(includeAlternateSkus && {
        'Alternate SKUs': sku.alternate_skus
          .map((sku) => sku.alternate_sku) // convert to string
          .join(', '),
      }),
      Reorder_Level: sku.reorder_level,
      Low_Stock_Alert_Quantity: sku.qty_limit_notify,
      Minimum_Stock: sku.oss_limit_notify,
      Wholesale_Price: this.costFormat.transform(sku?.wholesale_price),
      Retail_Price: this.costFormat.transform(sku?.retail_price),
      Links: productLinkStr,
      Shipment_Value: sku.shipment_value,
      Efficiency_Score: sku.efficiency_score,
      Package_Cost: this.costFormat.transform(sku?.package_cost),
      Labor_Cost: this.costFormat.transform(sku?.labor_cost),
      Fifo_Cost: this.costFormat.transform(sku?.base_price?.computed_fifo),
      Weight_Cost: this.costFormat.transform(sku?.base_price?.computed_weighted),
      Last_PO_Cost: this.costFormat.transform(sku?.base_price?.computed_lastpo),
      Default_Shipping: defaultShipping ? defaultShipping.name : '',
      Product_Weight: productWeightStr,
      Product_Dimension: this.getDimensionStringFormat({
        length: sku.product_length,
        width: sku.product_width,
        height: sku.product_height,
      }),
      Shipping_Weight: shippingWeightStr,
      Shipping_Dimension: this.getDimensionStringFormat({
        length: sku.shipping_length,
        width: sku.shipping_width,
        height: sku.shipping_height,
      }),
      Box_Weight: boxWeightStr,
      Box_Dimension: this.getDimensionStringFormat({
        length: sku.box_length,
        width: sku.box_width,
        height: sku.box_height,
      }),
      Carton_Weight: cartonWeightStr,
      Carton_Dimension: this.getDimensionStringFormat({
        length: sku.carton_length,
        width: sku.carton_width,
        height: sku.carton_height,
      }),
      Units_Per_Carton: sku.units_per_carton,
      Pallet_Weight: palletWeightStr,
      Pallet_Dimension: this.getDimensionStringFormat({
        length: sku.pallet_length,
        width: sku.pallet_width,
        height: sku.pallet_height,
      }),
      Cartons_Per_Pallet: sku.cartons_per_pallet,
      Units_Per_Pallet: sku.units_per_pallet,
      Palletized: sku.is_palletized ? 'YES' : 'NO',
      URL: `${window.location.origin}/inventory/${sku.id}/view`,
    };
    if (!this.UserPermission.can('exportUnitCost')) {
      delete skuData['Fifo_Cost'];
      delete skuData['Weight_Cost'];
      delete skuData['Last_PO_Cost'];
    }

    return skuData;
  }

  exportRfqsQuoteTypeToXlsx(rfqs :any[], includeAlternateSkus :boolean = false) {
    const fileName :string = includeAlternateSkus ? 'SKUsAlternateSKUs' : 'SKUs';
    // Records sheet
    const jsonData :any = [];

    let rfqList :any[] = [];
    rfqs.forEach((q) => {
      rfqList = rfqList.concat(q);
      if (q.variant_skus && q.variant_skus.length > 0) {
        rfqList = rfqList.concat(q.variant_skus);
      }
    });

    rfqList.forEach((q) => {
      jsonData.push({
        SKU: q.internal_sku,
        'Product Name': q.product_name,
        'Product Dept': q.product_dept,
        'Is Discontinued?': q.is_discontinued == 1 ? 'Discontinued' : 'Active',
        'Product Link': q.product_link,
        URL: window.location.origin + '/inventory/' + q.id + '/view',
      });
    });

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
    recordsSheet['A1'].s = { font: { bold: true } };
    recordsSheet['B1'].s = { font: { bold: true } };
    recordsSheet['C1'].s = { font: { bold: true } };
    recordsSheet['D1'].s = { font: { bold: true } };
    recordsSheet['E1'].s = { font: { bold: true } };
    recordsSheet['F1'].s = { font: { bold: true } };

    recordsSheet['!cols'] = [
      { wch: 25 },
      { wch: 60 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
    ];

    if (includeAlternateSkus) {
      recordsSheet['!cols'].splice(1, 0, { wch: 25 });
      recordsSheet['L1'].s = { font: { bold: true } };
    }

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName, '.xlsx');
  }

  exportAlternateRfqsToCsv(rfqs :any[]) {
    const fileName :string = 'SKUsAlternateSKUs';
    // Records sheet
    const jsonData :any = [];

    let rfqList :any[] = [];
    rfqs.forEach((q) => {
      rfqList = rfqList.concat(q);
      if (q.variant_skus && q.variant_skus.length > 0) {
        rfqList = rfqList.concat(q.variant_skus);
      }
    });

    rfqList.forEach((q) => {
      // rfqs.forEach((q) => {

      if (q.stock) {
        q.qty_incoming = q.stock.qty_incoming < 0 ? 0 : q.stock.qty_incoming;
        q.qty_pending = q.stock.qty_pending < 0 ? 0 : q.stock.qty_pending;
        q.qty_available = q.stock.qty_onhand - q.stock.qty_pending;
        q.qty_onhand = q.stock.qty_onhand;
      }

      // q.unit_costs = this.rfqSvc.getUnitCosts(q.qty_available, q.latest_purchase_orders, (q.base_price ? q.base_price.price : 0));
      q.unit_costs = this.rfqSvc.getSkuUnitCosts(q);

      jsonData.push({
        SKU: q.internal_sku,
        'Product Name': q.product_name,
        'Weighted Unit Cost':
          q.unit_costs.computed_weighted > 0
            ? this.curPipe.transform(q.unit_costs.computed_weighted)
            : '',
        'Is Discontinued?': q.is_discontinued == 1 ? 'Discontinued' : 'Active',
        'Alternate SKUs': q.alternate_skus
          ? q.alternate_skus.map((sku) => sku.alternate_sku).join(', ')
          : '',
        ...(this.featureFlagPipe.transform(
          this.featureFlag.REMOVE_PRODUCT_DEPARTMENT,
        )
          ? {}
          : { 'Product Department': q.product_dept }),
      });
    });

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  exportRfqsToPDF(rfqs :any[], alternateSkus :boolean = false) {
    this.getImgDataUrlValue(
      environment.clientConfig.assetImgDir + 'logo-print.png',
      (imgUri :any) => {
        // if (alternateSkus) {
        // 	this.creatRfqsAlternateSKUsPDF(rfqs, imgUri);
        // } else {
        this.creatRfqsPDF(rfqs, imgUri, alternateSkus);
        // }
      },
    );
  }

  creatRfqsPDF(
    rfqs :any,
    logoUri :string = '',
    includeAlternateSkus :boolean = false,
  ) {
    // prepare table data
    const tableData :any[] = [
      [
        { text: 'SKU', bold: true },
        { text: 'Product Name', bold: true },
        { text: 'Product Dept', bold: true },
        { text: 'Qty Incoming', bold: true, alignment: 'right' },
        { text: 'Qty On-Hand', bold: true, alignment: 'right' },
        { text: 'Qty Pending', bold: true, alignment: 'right' },
        { text: 'Qty Available', bold: true, alignment: 'right' },
        { text: 'UOM', bold: true }, // New column for UOM
        { text: 'Package Cost', bold: true, alignment: 'right' },
        { text: 'Labor Cost', bold: true, alignment: 'right' },
        { text: 'Shipping Weight', bold: true, alignment: 'right' },
        { text: 'Dimensional Weight', bold: true, alignment: 'right' },

        { text: 'Weighted Unit Cost', bold: true, alignment: 'right' },
        { text: 'Inventory Cost', bold: true, alignment: 'right' },
        { text: 'Is Discontinued?', bold: true },
        { text: 'URL', bold: true },
      ],
    ];
    // hide unit cost if not allowed
    if (!this.UserPermission.can('exportUnitCost')) {
      tableData[0].splice(11, 1);
    }

    if (includeAlternateSkus) {
      tableData[0].splice(1, 0, { text: 'Alternate SKUs', bold: true });
    }

    let rfqList :any[] = [];
    rfqs.forEach((q) => {
      rfqList = rfqList.concat(q);
      if (q.variant_skus && q.variant_skus.length > 0) {
        rfqList = rfqList.concat(q.variant_skus);
      }
    });

    rfqList.forEach((q) => {
      if (q.stock) {
        q.qty_incoming = q.stock.qty_incoming < 0 ? 0 : q.stock.qty_incoming;
        q.qty_pending = q.stock.qty_pending < 0 ? 0 : q.stock.qty_pending;
        q.qty_available = q.stock.qty_onhand - q.stock.qty_pending;
        q.qty_onhand = q.stock.qty_onhand;
      }

      q.unit_costs = this.rfqSvc.getSkuUnitCosts(q);

      let weightedCost = q.unit_costs.computed_weighted;
      if (weightedCost == 0 && q.base_price && q.base_price.computed_weighted) {
        weightedCost = q.base_price.computed_weighted;
      }

      const inventoryCost :number = q.qty_available * weightedCost;

      let shippingWeight = '';
      if (q.shipping_weight) {
        try {
          shippingWeight = JSON.parse(q.shipping_weight)
            .map((row) => row.wv + ' ' + row.mu)
            .join(' ');
        } catch (e) {}
      }
      let dimensionalWeight = '';
      if (q.dimensional_weight) {
        try {
          dimensionalWeight = JSON.parse(q.dimensional_weight)
            .map((row) => row.wv + ' ' + row.mu)
            .join(' ');
        } catch (e) {}
      }

      const data = [
        { text: q.internal_sku },
        {
          text: q.product_name,
          link: q.product_link,
          bold: q.product_link ? true : false,
        },
        // [{ text: q.product_name }, { text: q.product_link }],
        { text: q.product_dept },
        {
          text: q.qty_incoming,
          alignment: 'right',
        },
        {
          text: q.qty_onhand,
          alignment: 'right',
        },
        {
          text: q.qty_pending,
        },
        {
          text: q.qty_available,
          alignment: 'right',
        },
        { text: q?.type !== EItemType.KIT ? q?.unit_class?.name : '' },
        {
          text:
            q.package_cost && q.package_cost > 0
              ? this.costFormat.transform(q.package_cost)
              : '',
          alignment: 'right',
        },
        {
          text: this.costFormat.transform(
            q.efficiency_score && q.efficiency_score > 0
              ? this.laborCosts[q.efficiency_score]
              : 0,
          ),
          alignment: 'right',
        },
        { text: shippingWeight },
        { text: dimensionalWeight },

        {
          text: this.costFormat.transform(weightedCost > 0 ? weightedCost : 0),
          alignment: 'right',
        },
        {
          text: this.costFormat.transform(inventoryCost > 0 ? inventoryCost : 0),
          alignment: 'right',
        },
        { text: q.is_discontinued == 1 ? 'Discontinued' : 'Active' },
        { text: `${window.location.origin}/inventory/${q.id}/view` },
      ];

      // hide unit cost if not allowed
      if (!this.UserPermission.can('exportUnitCost')) {
        data.splice(11, 1);
      }

      if (includeAlternateSkus) {
        data.splice(1, 0, {
          text: q.alternate_skus
            ? q.alternate_skus
                .map((sku) => sku.alternate_sku) // convert to string
                .join(', ')
            : '---',
        });
      }

      tableData.push(data);
    });

    const today = new Date();

    // prepare header
    const headerContent = [
      {
        columns: [
          {
            alignment: 'left',
            width: 250,
            style: 'logo',
            ...(logoUri
              ? { image: logoUri, maxHeight: 100 }
              : { text: '', width: 250 }),
          },
          [
            {
              text: 'Request for Quotes',
              alignment: 'right',
              style: 'header',
            },
            {
              text: 'As of ' + this.datePipe.transform(today, 'mediumDate'),
              alignment: 'right',
            },
          ],
        ],
        margin: [0, 0, 0, 20],
      },
    ];
    const widths :any[] = [
      'auto', // SKU
      'auto', // Product Name
      '*', // Product Dept
      'auto', // Qty Incoming
      'auto', // Qty On-Hand
      'auto', // Qty Pending
      'auto', // Qty Available
      'auto', // UOM
      'auto', // Package Cost
      'auto', // Labor Cost
      'auto', // Shipping Weight
      'auto', // Dimensional Weight
      'auto', // Weighted Unit Cost
      'auto', // Inventory Cost
      'auto', // Is Discontinued?
      'auto', // URL
    ];
    // hide unit cost if not allowed
    if (!this.UserPermission.can('exportUnitCost')) {
      widths.splice(11, 1);
    }
    if (includeAlternateSkus) {
      widths.splice(1, 0, 'auto');
    }
    const tableContent :any[] = [
      {
        style: 'table',
        table: {
          widths: widths,
          headerRows: 1,
          body: tableData,
        },
        // layout: "lightHorizontalLines",
      },
    ];

    let pdfContent :any[] = [];
    pdfContent = pdfContent.concat(headerContent, tableContent);

    const documentDefinition = {
      pageSize: 'legal',
      pageOrientation: 'landscape',

      content: pdfContent,

      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        companyheader: {
          fontSize: 22,
          bold: true,
          margin: [0, 20, 0, 0],
          padding: [0, 0, 0, 0],
        },
        summarytitle: {
          fontSize: 14,
          bold: true,
        },
        header: {
          fontSize: 20,
          bold: true,
          margin: [0, 0, 0, 20],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 14,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        summary: {
          fontSize: 14,
          bold: false,
          margin: [10, 10, 10, 10],
          padding: [10, 10, 10, 10],
        },
        gmvTable: {
          margin: [0, 5, 0, 15],
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
        },
      },
    };
    Swal.close();
    const file = includeAlternateSkus ? 'SKUsAlternateSKUs.pdf' : 'SKUs.pdf';
    pdfMake.createPdf(documentDefinition).download(file);
  }

  creatRfqsAlternateSKUsPDF(rfqs :any, logoUri) {
    // prepare table data
    const tableData :any[] = [
      [
        { text: 'SKU', bold: true },
        { text: 'Product Name', bold: true },

        { text: 'Weighted Unit Cost', bold: true, alignment: 'right' },
        { text: 'Inventory Cost', bold: true, alignment: 'right' },
        { text: 'Is Discontinued?', bold: true },
        { text: 'Alternate SKUs', bold: true },
        { text: 'Product Department', bold: true },
      ],
    ];
    if (
      this.featureFlagPipe.transform(this.featureFlag.REMOVE_PRODUCT_DEPARTMENT)
    ) {
      const index = tableData[0].findIndex(
        (item) => item.text === 'Product Department',
      );
      if (index !== -1) {
        tableData[0].splice(index, 1);
      }
    }

    let rfqList :any[] = [];
    rfqs.forEach((q) => {
      rfqList = rfqList.concat(q);
      if (q.variant_skus && q.variant_skus.length > 0) {
        rfqList = rfqList.concat(q.variant_skus);
      }
    });

    rfqList.forEach((q) => {
      // rfqs.forEach((q) => {

      if (q.stock) {
        q.qty_incoming = q.stock.qty_incoming < 0 ? 0 : q.stock.qty_incoming;
        q.qty_pending = q.stock.qty_pending < 0 ? 0 : q.stock.qty_pending;
        q.qty_available = q.stock.qty_onhand - q.stock.qty_pending;
        q.qty_onhand = q.stock.qty_onhand;
      }

      // q.unit_costs = this.rfqSvc.getUnitCosts(q.qty_available, q.latest_purchase_orders, (q.base_price ? q.base_price.price : 0));
      q.unit_costs = this.rfqSvc.getSkuUnitCosts(q);

      const inventoryCost :number =
        q.qty_available * q.unit_costs.computed_weighted;

      const data = [
        { text: q.internal_sku },
        {
          text: q.product_name,
          link: q.product_link,
          bold: q.product_link ? true : false,
        },
        {
          text:
            q.unit_costs.computed_weighted > 0
              ? this.curPipe.transform(q.unit_costs.computed_weighted)
              : '',
          alignment: 'right',
        },
        {
          text: inventoryCost > 0 ? this.curPipe.transform(inventoryCost) : '',
          alignment: 'right',
        },
        { text: q.is_discontinued == 1 ? 'Discontinued' : 'Active' },
        {
          text: q.alternate_skus
            ? q.alternate_skus.map((sku) => sku.alternate_sku).join(', ')
            : '',
        },
        { text: q.product_dept },
      ];

      tableData.push(data);
    });
    const today = new Date();

    // prepare header
    const headerContent = [
      {
        columns: [
          {
            alignment: 'left',
            image: logoUri,
            width: 250,
            style: 'logo',
          },
          [
            {
              text: 'Request for Quotes',
              alignment: 'right',
              style: 'header',
            },
            {
              text: 'As of ' + this.datePipe.transform(today, 'mediumDate'),
              alignment: 'right',
            },
          ],
        ],
      },
    ];

    const tableContent :any[] = [
      {
        style: 'table',
        table: {
          widths: ['auto', '*', 'auto', 'auto', 'auto', 'auto'],
          headerRows: 1,
          body: tableData,
        },
        // layout: "lightHorizontalLines",
      },
    ];

    let pdfContent :any[] = [];
    pdfContent = pdfContent.concat(headerContent, tableContent);

    const documentDefinition = {
      pageSize: 'legal',
      pageOrientation: 'landscape',

      content: pdfContent,

      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        companyheader: {
          fontSize: 22,
          bold: true,
          margin: [0, 20, 0, 0],
          padding: [0, 0, 0, 0],
        },
        summarytitle: {
          fontSize: 14,
          bold: true,
        },
        header: {
          fontSize: 20,
          bold: true,
          margin: [0, 0, 0, 20],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 14,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        summary: {
          fontSize: 14,
          bold: false,
          margin: [10, 10, 10, 10],
          padding: [10, 10, 10, 10],
        },
        gmvTable: {
          margin: [0, 5, 0, 15],
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
        },
      },
    };
    Swal.close();
    pdfMake.createPdf(documentDefinition).download('SKUsAlternateSKUs.pdf');
  }

  exportDuplicateSKU(data :any) {
    const fileName :string = 'DuplicateSKU';
    // Records sheet
    const jsonData :any = [];

    data.forEach((skus) => {
      skus.data.forEach((q) => {
        jsonData.push({
          ID: q.id,
          SKU: q.sku,
          TYPE: q.type,
        });
      });
    });

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
    recordsSheet['A1'].s = { font: { bold: true } };
    recordsSheet['B1'].s = { font: { bold: true } };
    recordsSheet['C1'].s = { font: { bold: true } };

    recordsSheet['!cols'] = [{ wch: 25 }, { wch: 60 }, { wch: 25 }];

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName, '.xlsx');
  }

  exportInvStocksRfq(data :any, showLocations :boolean = false) {
    const fileName :string = 'SKU_Inventory_Stocks';
    // Records sheet
    const jsonData :any = [];
    let totalInventoryValue :number = 0;

    data.forEach((q :any, idx :number) => {
      const stocks :any = [];
      const total :any = {
        qty_incoming: 0,
        qty_onhand: 0,
        qty_pending: 0,
        qty_available: 0,
        warehouse: ' ',
      };

      if (q.location_stocks && q.location_stocks.length > 0) {
        q.location_stocks.forEach((qls :any) => {
          total.qty_incoming += qls.qty_incoming;
          total.qty_onhand += qls.qty_onhand;
          total.qty_pending += qls.qty_pending;
          total.qty_available += qls.qty_onhand - qls.qty_pending;

          const stock :any = {
            warehouse: qls.warehouse ? qls.warehouse.location_name : 'General',
            qty_incoming: qls.qty_incoming,
            qty_onhand: qls.qty_onhand,
            qty_pending: qls.qty_pending,
            qty_available: qls.qty_onhand - qls.qty_pending,
          };

          stocks.push(stock);

          if (qls.stock_locations.length > 0 && showLocations) {
            qls.stock_locations.forEach((location :any) => {
              const stock :any = {
                warehouse: '',
                location: location.location_name ? location.location_name : '',
                qty_incoming: '',
                qty_onhand: location.pivot?.onhand_quantity
                  ? location.pivot?.onhand_quantity
                  : 0,
                qty_pending: '',
                qty_available: '',
              };

              stocks.push(stock);
            });
          }
        });
      }

      totalInventoryValue += q.unit_costs.computed_weighted
        ? total.qty_onhand * q.unit_costs.computed_weighted
        : 0;

      if (!q.location_stocks.length) {
        stocks.push(total);
      } else if (q.location_stocks.length > 1) {
        total.warehouse = 'Total';
        stocks.push(total);
      }
      // var qty_incoming = 0;
      // var qty_onhand = 0;
      // var qty_pending = 0;
      // var qty_available = 0;

      // if(q.stock !== null) {
      // 	if(q.stock.qty_incoming !== undefined) {
      // 		qty_incoming = q.stock.qty_incoming > 0 ? q.stock.qty_incoming : 0;
      // 	}

      // 	if(q.stock.qty_onhand !== undefined) {
      // 		qty_onhand = q.stock.qty_onhand > 0 ? q.stock.qty_onhand : 0;
      // 	}

      // 	if(q.stock.qty_pending !== undefined) {
      // 		qty_pending = q.stock.qty_pending > 0 ? q.stock.qty_pending : 0;
      // 	}

      // 	if(q.stock.qty_pending !== undefined) {
      // 		qty_available = q.stock.qty_onhand > 0 ? q.stock.qty_onhand - q.stock.qty_pending : 0 - q.stock.qty_pending;
      // 	}
      // }

      // stocks.forEach((stock: any, idx) => {
      // 	jsonData.push({
      // 		ID: idx > 0 ? "" : q.id,
      // 		SKU: idx > 0 ? "" : q.internal_sku,
      // 		"Product Name": q.product_name,
      // 		Warehouse: stock.label,
      // 		Incoming: stock.qty_incoming,
      // 		Onhand: stock.qty_onhand,
      // 		Pending: stock.qty_pending,
      // 		Available: stock.qty_available,
      // 		"Weighted Cost": idx > 0 ? "" : q.unit_costs.computed_weighted ? q.unit_costs.computed_weighted : 0,
      // 		"FIFO Cost": idx > 0 ? "" : q.unit_costs.computed_fifo ? q.unit_costs.computed_fifo : 0,
      // 		"Last PO Cost": idx > 0 ? "" : q.unit_costs.computed_lastpo ? q.unit_costs.computed_lastpo : 0
      // 	});
      // });

      stocks.forEach((stock :any, index :number) => {
        let newStock :any = {
          ID: q.id,
          SKU: q.internal_sku,
          'Product Name': q.product_name,
          Warehouse: stock.warehouse,
        };

        if (showLocations) {
          newStock = {
            ...newStock,
            Location: stock.location,
          };
        }

        newStock = {
          ...newStock,
          Incoming: stock.qty_incoming,
          Onhand: stock.qty_onhand,
          Pending: stock.qty_pending,
          Available: stock.qty_available,
          'Weighted Cost': q.unit_costs.computed_weighted
            ? q.unit_costs.computed_weighted
            : 0,
          'FIFO Cost': q.unit_costs.computed_fifo ? q.unit_costs.computed_fifo : 0,
          'Last PO Cost': q.unit_costs.computed_lastpo
            ? q.unit_costs.computed_lastpo
            : 0,
          'Inventory Value': q.unit_costs.computed_weighted
            ? stock.qty_onhand * q.unit_costs.computed_weighted
            : 0,
        };

        if (!this.UserPermission.can('exportUnitCost')) {
          delete newStock['Weighted Cost'];
          delete newStock['FIFO Cost'];
          delete newStock['Last PO Cost'];
        }

        jsonData.push(newStock);
      });
    });

    const headerJsonData :any = [];
    headerJsonData.push({
      '': '',
      ' ': 'Total Inventory Value',
      '  ': totalInventoryValue,
    });

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
    const headerSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(headerJsonData);

    recordsSheet['A1'].s = { font: { bold: true } };
    recordsSheet['B1'].s = { font: { bold: true } };
    recordsSheet['C1'].s = { font: { bold: true } };
    recordsSheet['D1'].s = { font: { bold: true } };
    recordsSheet['E1'].s = { font: { bold: true } };
    recordsSheet['F1'].s = { font: { bold: true } };
    recordsSheet['G1'].s = { font: { bold: true } };

    if (this.UserPermission.can('exportUnitCost')) {
      recordsSheet['H1'].s = { font: { bold: true } };
      recordsSheet['I1'].s = { font: { bold: true } };
      recordsSheet['J1'].s = { font: { bold: true } };
    }

    recordsSheet['!cols'] = [
      { wch: 25 },
      { wch: 60 },
      { wch: 60 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
    ];

    const wb :XLSX.WorkBook = XLSX.utils.book_new();

    XLSX.utils.sheet_add_json(headerSheet, jsonData, { header: [], origin: 'A4' });
    XLSX.utils.book_append_sheet(wb, headerSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  exportInvStocksKit(data :any) {
    const fileName :string = 'InvStocksKit';
    // Records sheet
    const jsonData :any = [];

    data.forEach((q) => {
      let qty_incoming = 0;
      let qty_onhand = 0;
      let qty_pending = 0;
      let qty_available = 0;

      if (q.stock !== null) {
        if (q.stock.qty_incoming !== undefined) {
          qty_incoming = q.stock.qty_incoming > 0 ? q.stock.qty_incoming : 0;
        }

        if (q.stock.qty_onhand !== undefined) {
          qty_onhand = q.stock.qty_onhand > 0 ? q.stock.qty_onhand : 0;
        }

        if (q.stock.qty_pending !== undefined) {
          qty_pending = q.stock.qty_pending > 0 ? q.stock.qty_pending : 0;
        }

        if (q.stock.qty_pending !== undefined) {
          qty_available =
            q.stock.qty_onhand > 0
              ? q.stock.qty_onhand - q.stock.qty_pending
              : 0 - q.stock.qty_pending;
        }
      }

      const kitStock :any = {
        ID: q.id,
        SKU: q.internal_sku,
        'Product Name': q.product_name,
        Incoming: qty_incoming,
        Onhand: qty_onhand,
        Pending: qty_pending,
        Available: qty_available,
        'Weighted Cost': q.unit_costs.computed_weighted
          ? q.unit_costs.computed_weighted
          : 0,
        'FIFO Cost': q.unit_costs.computed_fifo ? q.unit_costs.computed_fifo : 0,
        'Last PO Cost': q.unit_costs.computed_lastpo
          ? q.unit_costs.computed_lastpo
          : 0,
      };
      if (!this.UserPermission.can('exportUnitCost')) {
        delete kitStock['Weighted Cost'];
        delete kitStock['FIFO Cost'];
        delete kitStock['Last PO Cost'];
      }
      jsonData.push(kitStock);
    });

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
    recordsSheet['A1'].s = { font: { bold: true } };
    recordsSheet['B1'].s = { font: { bold: true } };
    recordsSheet['C1'].s = { font: { bold: true } };
    recordsSheet['D1'].s = { font: { bold: true } };
    recordsSheet['E1'].s = { font: { bold: true } };
    recordsSheet['F1'].s = { font: { bold: true } };
    recordsSheet['G1'].s = { font: { bold: true } };
    if (this.UserPermission.can('exportUnitCost')) {
      recordsSheet['H1'].s = { font: { bold: true } };
      recordsSheet['I1'].s = { font: { bold: true } };
      recordsSheet['J1'].s = { font: { bold: true } };
    }
    recordsSheet['!cols'] = [
      { wch: 25 },
      { wch: 60 },
      { wch: 60 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
    ];

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  exportFullMasterSkuToExcel(rfqs :any[]) :void {
    const filename :string = 'full-master-sku';
    // Records sheet
    const masterSkuData :any = [];

    let rfqList :any[] = [];
    rfqs.forEach((q) => {
      rfqList = rfqList.concat(q);
    });

    rfqList.forEach((sku, key) => {
      // SET DATA
      const boxWeightArr = this.getParseableData(sku.box_weight);
      const productWeightArr = this.getParseableData(sku.product_weight);
      const shippingWeightArr = this.getParseableData(sku.shipping_weight);
      const cartonWeightArr = this.getParseableData(sku.carton_weight);
      const palletWeightArr = this.getParseableData(sku.pallet_weight);
      const productLinkArr = this.getParseableData(sku.product_link);
      // const defaultShipping = shippingCarriers.find((carrier) => carrier.id === sku.default_shipping);

      let boxWeightStr = '';
      let productWeightStr = '';
      let shippingWeightStr = '';
      let cartonWeightStr = '';
      let palletWeightStr = '';
      let productLinkStr = '';

      if (boxWeightArr && typeof boxWeightArr === 'object') {
        boxWeightStr = boxWeightArr.reduce(
          (acc, value) =>
            acc ? `${acc}, ${value.wv}${value.mu}` : `${value.wv}${value.mu}`,
          '',
        );
      }

      if (productWeightArr && typeof productWeightArr === 'object') {
        productWeightStr = productWeightArr.reduce(
          (acc, value) =>
            acc ? `${acc}, ${value.wv}${value.mu}` : `${value.wv}${value.mu}`,
          '',
        );
      }

      if (shippingWeightArr && typeof shippingWeightArr === 'object') {
        shippingWeightStr = shippingWeightArr.reduce(
          (acc, value) =>
            acc ? `${acc}, ${value.wv}${value.mu}` : `${value.wv}${value.mu}`,
          '',
        );
      }

      if (cartonWeightArr && typeof cartonWeightArr === 'object') {
        cartonWeightStr = cartonWeightArr.reduce(
          (acc, value) =>
            acc ? `${acc}, ${value.wv}${value.mu}` : `${value.wv}${value.mu}`,
          '',
        );
      }

      if (palletWeightArr && typeof palletWeightArr === 'object') {
        palletWeightStr = palletWeightArr.reduce(
          (acc, value) =>
            acc ? `${acc}, ${value.wv}${value.mu}` : `${value.wv}${value.mu}`,
          '',
        );
      }

      if (
        productLinkArr &&
        typeof productLinkArr === 'object' &&
        productLinkArr.length
      ) {
        productLinkStr = productLinkArr.reduce(
          (acc, value) => (acc ? `${acc}, ${value.lv}` : value.lv),
          '',
        );
      }

      const master_sku =
        sku.variant_parent_id === 0 ? sku?.internal_sku : sku?.parent?.internal_sku;
      const variant_sku = sku?.variant_parent_id === 0 ? '' : sku?.internal_sku;

      const locations = [];
      const warehouses = [];
      const alternate_skus = [];
      const tags = [];

      sku.location_stocks?.forEach((stock) => {
        if (stock.stock_locations) {
          stock.stock_locations?.forEach((loc) => {
            locations.push(loc.location_name);
          });
        }
        if (stock.warehouse) {
          if (warehouses.indexOf(stock.warehouse.location_name) === -1) {
            // If the warehouse isn't found in the array, push its name to array
            warehouses.push(stock.warehouse.location_name);
          }
        }
      });

      sku.alternate_skus.forEach((alt) => {
        alternate_skus.push(alt.alternate_sku);
      });

      sku.tags.forEach((tag) => {
        if (tag.tag_details != null) {
          tags.push(tag.tag_details.name);
        }
      });

      const skuData :any = {
        'Product Name': sku.product_name,
        'Product URL': `${window.location.origin}/inventory/${sku.id}/view`,
        'Master SKU #': master_sku,
        'Variant SKU #': variant_sku,
        'Alternate SKUs': alternate_skus.join(', '),
        UPC: sku.upc,
        'Location Warehouse': warehouses.join(', '),
        'Unit Cost (Weighted)': this.costFormat.transform(
          sku?.base_price?.computed_weighted,
        ),
        'Unit Cost (FIFO)': this.costFormat.transform(
          sku?.base_price?.computed_fifo,
        ),
        'Unit Cost (Latest PO)': this.costFormat.transform(
          sku?.base_price?.computed_lastpo,
        ),
        'Wholesale Cost': this.costFormat.transform(sku?.wholesale_price),
        'Retail Cost': this.costFormat.transform(sku?.retail_price),
        'Qty Pending': sku.stock?.qty_pending || 0,
        'Qty On Hand': sku.stock?.qty_onhand || 0,
        'Qty Incoming': sku.stock?.qty_incoming || 0,
        'Qty Available':
          (sku.stock?.qty_onhand || 0) - (sku.stock?.qty_pending || 0),
        UOM: sku?.unit_class?.name,
        'Re-Order Level': sku.reorder_level,
        'Low Stock Alert Quantity': sku.qty_limit_notify,
        'Minimum Stock': sku.oss_limit_notify,
        'Last Month Sale Qty':
          sku.last_month_sale != null ? sku.last_month_sale.quantity : 0,
        'Product Description': this.removeTagsOnString(sku.product_description),
        'Product Category': sku.product_category,
        'Product Sub Category': sku.sub_category,
        'Preferred Vendor': sku.preferred_vendor,
        'Product Tags': tags.join(', '),
        'Product Weight': productWeightStr,
        'Product Dimensions': this.getDimensionStringFormat({
          length: sku.product_length,
          width: sku.product_width,
          height: sku.product_height,
        }),
        'Shipping Weight': shippingWeightStr,
        'Shipping Dimensions': this.getDimensionStringFormat({
          length: sku.shipping_length,
          width: sku.shipping_width,
          height: sku.shipping_height,
        }),
        'Box Weight': boxWeightStr,
        'Box Dimensions': this.getDimensionStringFormat({
          length: sku.box_length,
          width: sku.box_width,
          height: sku.box_height,
        }),
        'Carton Weight': cartonWeightStr,
        'Carton Dimensions': this.getDimensionStringFormat({
          length: sku.carton_length,
          width: sku.carton_width,
          height: sku.carton_height,
        }),
        'Pallet Weight': palletWeightStr,
        'Pallet Dimensions': this.getDimensionStringFormat({
          length: sku.pallet_length,
          width: sku.pallet_width,
          height: sku.pallet_height,
        }),
        'Cartons per pallet': sku.cartons_per_pallet,
        'Units per pallet': sku.units_per_pallet,
        'Units per carton': sku.units_per_carton,
        'Product Links': productLinkStr,
        Materials: this.getParseableData(sku.materials).reduce(
          (acc, value) => (acc ? `${acc}, ${value.mv}` : value.mv),
          '',
        ),
        'Link to Product Photo': sku.photo,
        'Link to Product Design': sku.product_design,
      };
      if (!this.UserPermission.can('exportUnitCost')) {
        delete skuData['Unit Cost (Weighted)'];
        delete skuData['Unit Cost (FIFO)'];
        delete skuData['Unit Cost (Latest PO)'];
      }
      masterSkuData.push(skuData);
    });

    const masterSkusRecordSheet :XLSX.WorkSheet =
      XLSX.utils.json_to_sheet(masterSkuData);
    this.downloadExelFile(masterSkusRecordSheet, filename, '.xlsx');
    Swal.close();
  }

  private removeTagsOnString(inputString :string = '') :string {
    if (!inputString) {
      return '';
    }
    const div = document.createElement('div');
    div.innerHTML = inputString.replace(/<br\s*[\/]?>/gi, '\n');

    return div.textContent || div.innerText || '';
  }

  private exportToExcel(data :any[], fileName :string = 'SKU_Details') :void {
    const masterSkusRecordSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
    this.downloadExelFile(masterSkusRecordSheet, fileName, '.xlsx');
  }

  private exportToCsv(data :any[], fileName :string = 'SKU_Details') :void {
    const masterSkusRecordSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
    const csvContent = XLSX.utils.sheet_to_csv(masterSkusRecordSheet);
    const excelBuffer = new TextEncoder().encode(csvContent);
    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  private getCustomFieldsData(sku :any, customFields :any[]) :any {
    const customFieldsData :any = {
      SKU: sku.internal_sku,
      'Product Name': sku.product_name,
    };

    customFields.forEach((field) => {
      const value = sku.custom_field_values.find(
        (customField) => customField.custom_field_id === field.id,
      );

      customFieldsData[field.label] =
        (value?.value_string || value?.value_date || value?.value_number) ?? null;
    });

    return customFieldsData;
  }

  private generateCustomFieldsDataForExport(
    rfqs :any[],
    customFields :any[],
  ) :any[] {
    let rfqList = [];

    rfqs.forEach((q) => {
      rfqList.push(q);
      if (q.variant_skus && q.variant_skus.length > 0) {
        rfqList = rfqList.concat(q.variant_skus);
      }
    });

    return rfqList.map((sku) => this.getCustomFieldsData(sku, customFields));
  }

  exportWithCustomFields(rfqs :any[], customFields :any[], type :string) :void {
    const fileName = 'SKU_With_Custom_Fields';
    const data = this.generateCustomFieldsDataForExport(rfqs, customFields);
    if (type === FileTypeConstant.CSV) {
      this.exportToCsv(data, fileName);
    } else if (type === FileTypeConstant.XLSX) {
      this.exportToExcel(data, fileName);
    }
    Swal.close();
  }

  exportRfqsToPdfV2(
    rfqs :any[],
    logo :string = '',
    alternateSkus :boolean = false,
  ) :void {
    if (!logo) {
      return this.creatRfqsPDF(rfqs, logo, alternateSkus);
    }

    this.getImgDataUrlValue(logo, (imgUri :any) => {
      this.creatRfqsPDF(rfqs, imgUri, alternateSkus);
    });
  }
}
